import React, { useState, useRef, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  Tabs,
  Tab,
  Box,
  Button,
  AppBar,
  Toolbar,
  Typography,
} from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import TextToQR from './components/TextToQR';
import WiFiToQR from './components/WiFiToQR';
import TransferToQR from './components/TransferToQR';
import ImageToQR from './components/ImageToQR';
import LinkToQR from './components/LinkToQR';
import PDFBreaker from './components/PDFBreaker';
import { AuthProvider, useAuth } from './components/AuthProvider';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function AppContent() {
  const [tabValue, setTabValue] = useState(0);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const { user, handleLogout, setShowAuthDialog } = useAuth();
  const qrRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setQrImageUrl(null);
  };

  const handleQRGenerated = (imageUrl: string) => {
    setQrImageUrl(imageUrl);
  };

  useEffect(() => {
    if (qrImageUrl && qrRef.current) {
      qrRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [qrImageUrl]);

  // If we're on the PDF breaker page, render it directly
  if (location.pathname === '/pdf-breaker') {
    return (
      <>
        <AppBar position="static" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              PDF Breaker
            </Typography>
            <Button color="inherit" component={Link} to="/">
              QR Generator
            </Button>
            {user ? (
              <Button color="inherit" onClick={handleLogout}>
                Cerrar sesión
              </Button>
            ) : (
              <Button color="inherit" onClick={() => setShowAuthDialog(true)}>
                Iniciar sesión
              </Button>
            )}
          </Toolbar>
        </AppBar>
        <Container maxWidth="lg" sx={{ py: 4 }}>
          <PDFBreaker />
        </Container>
      </>
    );
  }

  // Main QR generator page
  return (
    <>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            QR Bert Generator
          </Typography>
          <Button color="inherit" component={Link} to="/pdf-breaker">
            PDF Breaker
          </Button>
          {user ? (
            <Button color="inherit" onClick={handleLogout}>
              Cerrar sesión
            </Button>
          ) : (
            <Button color="inherit" onClick={() => setShowAuthDialog(true)}>
              Iniciar sesión
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 4 }}>
        <Paper elevation={3}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-flexContainer': {
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 1
              },
              '& .MuiTab-root': {
                minWidth: 'auto',
                flex: '1 1 auto'
              }
            }}
          >
            <Tab label="Texto a QR" />
            <Tab label="WIFI a QR" />
            <Tab label="Transferencia a QR" />
            <Tab label="Img a QR" />
            <Tab label="Link a QR" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <TextToQR onQRGenerated={handleQRGenerated} />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <WiFiToQR onQRGenerated={handleQRGenerated} />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <TransferToQR onQRGenerated={handleQRGenerated} />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ImageToQR onQRGenerated={handleQRGenerated} />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <LinkToQR onQRGenerated={handleQRGenerated} />
          </TabPanel>

          {qrImageUrl && (
            <Box ref={qrRef} sx={{ mt: 2, textAlign: 'center', p: 3 }}>
              <img src={qrImageUrl} alt="QR Generado" style={{ maxWidth: '100%' }} />
            </Box>
          )}
        </Paper>
      </Container>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/" element={<AppContent />} />
          <Route path="/pdf-breaker" element={<AppContent />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
